'use client';

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  X,
} from 'lucide-react';
import * as React from 'react';

import { cn } from './cn';

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success';
export type AlertVariant = 'standard' | 'outlined' | 'filled';

export interface AlertProps
  // `title` is the alert's heading here, not the native tooltip attribute —
  // omitting it prevents `ReactNode` colliding with `HTMLAttributes.title`.
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Which semantic family paints the surface, border and ink. */
  severity?: AlertSeverity;
  /**
   * `standard` (tinted surface, the MUI default), `outlined` (transparent,
   * tinted border) and `filled` (solid severity fill, contrast ink) — the three
   * MUI `Alert` variants, kept because the sweep maps call sites 1:1.
   */
  variant?: AlertVariant;
  /**
   * Override the severity glyph. `false` removes the slot entirely (MUI's
   * escape hatch for an alert whose icon would duplicate an adjacent one).
   */
  icon?: React.ReactNode | false;
  /** Renders the dismiss affordance. Receives the click event. */
  onClose?: (event: React.SyntheticEvent) => void;
  /** Accessible name for the dismiss button. Defaults to "Dismiss". */
  closeLabel?: string;
  /** Trailing slot for a link or button (MUI `action`). */
  action?: React.ReactNode;
  /**
   * Convenience for the common single-title shape. `AlertTitle` as a child
   * works identically; this only saves the import at simple call sites.
   */
  title?: React.ReactNode;
}

/**
 * The new-system alert: the MUI-free replacement for `@mui/material/Alert`
 * (125 files), Token-driven, `forwardRef`, `cn()`-merged, no `sx`.
 *
 * PARITY SOURCE, AND WHERE IT DELIBERATELY DIFFERS. The colour rows are the
 * SAME rows `ui/chip` already ships (see the `@layer components` recipe in
 * `src/tailwind.css`), which are themselves transcribed from the app's own
 * `getChipSx` — so an alert and the chip beside it read as one family, and the
 * scheme resolution (`[data-minerva-color-scheme="dark"]`) already exists there for
 * the same reason. Measured contrast of the light ink on the `-50` tint, all
 * AA for body text: error `#a01414` on `#fff1f1` 7.33:1, warning `#805113` on
 * `#fdfce9` 6.53:1, success `#1e5d34` on `#f1fcf5` 7.48:1, info `#626571` on
 * `#f5f7f8` 5.40:1.
 *
 * THE DARK NUMBERS ARE MEASURED ON THE TINT, NOT ON THE PAGE, and this line used
 * to overstate them as "9.7-12.2:1 on the dark surface". Those are the values
 * against the BARE canvas (`#0F1117`) and no alert is drawn on it: the standard
 * variant sits on a 14% mix of its own `-500`, and the ink has to clear AA on
 * THAT. Measured there: error `#ff6666` 5.81:1, warning `#f0ce1f` 9.53:1, success
 * `#71d592` 8.59:1. All three still clear AA, so this is a correction of the
 * record, not a defect (design review round 1, D10) — but the lower numbers are
 * the honest ones, and the `filled` family is where the pair actually fails (see
 * the `filled` note in `src/tailwind.css`).
 *
 * The two deliberate deltas from MUI, both recorded rather than silent:
 *  - RADIUS is `rounded-lg` (8px), not MUI's 4px `shape.borderRadius`. AGENTS.md
 *    "UI System" §7 pins menus/dialogs/popovers to the 8px radius, and an alert
 *    sits in the same stack of surfaces; this is the "may look more modern"
 *    allowance, not drift.
 *  - THE GLYPH is a 1rem `size-4` box, not MUI's 22px `SvgIcon`. The
 *    consistency instrument's `icon-ratio` check declares one icon-to-label
 *    relationship for the whole programme (a 1rem square against the 0.875rem
 *    control type size); a 22px glyph beside 14px text is exactly the defect
 *    that check was written for, so the alert adopts the house ratio.
 *
 * WHY THE ICON IS `aria-hidden`. `role="alert"` announces the alert's TEXT;
 * the glyph is redundant with it, and a screen reader announcing "error icon"
 * before the message is noise. The severity travels on `data-alert-severity`
 * for styling and on `role="alert"` for assistive tech.
 *
 * ACCESSIBILITY. The root is `role="alert"` like MUI's (assertive, so it is
 * announced when it appears). A caller that renders the alert already present
 * on first paint — a permissions banner, say — should pass `role="status"`
 * through `...rest` instead, so the page does not shout on load; MUI had no
 * such distinction and call sites compensated with `role` overrides, which is
 * why `role` is not hardcoded past the default.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(
    {
      action,
      children,
      className,
      closeLabel = 'Dismiss',
      icon,
      onClose,
      severity = 'info',
      title,
      variant = 'standard',
      ...rest
    },
    ref
  ) {
    const Icon = DEFAULT_ICON[severity];
    const showIcon = icon !== false;

    return (
      <div
        ref={ref}
        className={cn(
          'minerva-alert flex w-full items-start gap-[calc(var(--minerva-spacing)*1.5)] rounded-lg px-[calc(var(--minerva-spacing)*2)] py-[calc(var(--minerva-spacing)*1.25)] text-sm',
          className
        )}
        data-alert-severity={severity}
        data-alert-variant={variant}
        role="alert"
        {...rest}
      >
        {showIcon ? (
          <span
            aria-hidden="true"
            className="mt-[0.1875rem] flex shrink-0 items-center [&_svg]:size-4"
            data-slot="alert-icon"
          >
            {icon ?? <Icon />}
          </span>
        ) : null}
        {/* Name the real body for scoped full-width content rules; styling
            must not depend on a vendor message wrapper that is no longer here. */}
        <div
          data-slot="alert-content"
          className="flex min-w-0 flex-1 flex-col gap-1"
        >
          {title !== undefined ? <AlertTitle>{title}</AlertTitle> : null}
          {children}
          {action ? (
            <div
              className="flex items-center gap-2 pt-1"
              data-slot="alert-action"
            >
              {action}
            </div>
          ) : null}
        </div>
        {onClose ? (
          <button
            aria-label={closeLabel}
            className="flex size-6 shrink-0 items-center justify-center rounded transition-colors hover:bg-accent focus-visible:outline-offset-2 focus-visible:[outline:2px_solid_var(--color-ring)] [&_svg]:size-3.5"
            data-slot="alert-close"
            onClick={onClose}
            type="button"
          >
            <X />
          </button>
        ) : null}
      </div>
    );
  }
);
Alert.displayName = 'Alert';

/**
 * The alert's heading line. `font-semibold` and no colour of its own: it
 * inherits the recipe's ink, which is what makes the severity read on the
 * title while the body copy stays the family's measured stop. MUI's
 * `AlertTitle` behaved the same way (weight 500, inherited colour).
 */
export const AlertTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertTitle({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cn('minerva-alert-title font-semibold', className)}
      {...rest}
    />
  );
});
AlertTitle.displayName = 'AlertTitle';

/**
 * The alert's body slot. Named for the shadcn vocabulary and for parity with
 * `AlertTitle`; a bare string child works too, so a simple
 * `<Alert severity="error">Save failed</Alert>` needs no wrapper.
 */
export const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDescription({ className, ...rest }, ref) {
  return (
    <div
      ref={ref}
      className={cn('minerva-alert-description leading-[1.45]', className)}
      {...rest}
    />
  );
});
AlertDescription.displayName = 'AlertDescription';

const DEFAULT_ICON: Record<AlertSeverity, React.ComponentType> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CheckCircle2,
};
